import { useQuery } from '@tanstack/react-query';
import { apiClient as api } from '@plerionhq/design-system';
import {
  ACCESS_GRAPH_FLAG,
  ASSET_OWNERSHIP_FLAG,
  ASSET_PACKAGE_FILTER_FLAG,
  AWS_ORGANIZATION_ONBOARDING_FLAG,
  type FlagContext,
  INTEGRATION_USER_DEFINED_TAGS_FLAG,
  isClientFeatureEnabled,
  isDevOverrideEnabled,
} from './client-feature-flags';

export {
  ACCESS_GRAPH_FLAG,
  ASSET_OWNERSHIP_FLAG,
  ASSET_PACKAGE_FILTER_FLAG,
  AWS_ORGANIZATION_ONBOARDING_FLAG,
  type FlagContext,
  CLIENT_FEATURE_FLAGS,
  INTEGRATION_USER_DEFINED_TAGS_FLAG,
  isClientFeatureEnabled,
} from './client-feature-flags';

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['featureFlags'],
    queryFn: () =>
      api
        .get<string[]>('/api/base/organization/feature-flags')
        .then((res) => res.data),
    staleTime: 300000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * Whether a flag is on, honouring the dev override. Prefer the hook for a
 * single known flag; this is for callers that resolve the flag name at
 * runtime, such as a navigation table gating one of its entries.
 */
export function isFeatureEnabled(
  flag: string,
  enabledFeatures: string[] | undefined,
  context: FlagContext = {},
): boolean {
  if (isDevOverrideEnabled(flag) || isClientFeatureEnabled(flag, context)) {
    return true;
  }
  return !!enabledFeatures?.includes(flag);
}

/**
 * Gates the Tags section on the integration settings page and its editor.
 * The backend accepts user-defined tags regardless of this flag; it decides
 * what the UI offers.
 */
export function useIntegrationUserDefinedTagsEnabled(): boolean {
  return isClientFeatureEnabled(INTEGRATION_USER_DEFINED_TAGS_FLAG);
}

/**
 * Gates the Access tab on an asset's page: on for ACCESS_GRAPH_ORGS, or in a
 * browser with the localStorage override.
 */
export function useAccessGraphEnabled(): boolean {
  return isClientFeatureEnabled(ACCESS_GRAPH_FLAG, useFlagContext());
}

const useFlagContextQuery = () =>
  useQuery({
    queryKey: ['api', 'flag-context'],
    queryFn: () =>
      api
        .get<{ organizationId: string }>('/api/base/users/me')
        .then((res) => ({ organizationId: res.data.organizationId })),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

/** The signed-in user's organization, for flags decided per organization. */
export function useFlagContext(): FlagContext {
  return useFlagContextQuery().data ?? {};
}

/**
 * Whether the organization has been read, so a flag decided per organization
 * is final. Until then such a flag reads as off.
 */
export function useIsFlagContextKnown(): boolean {
  const { isSuccess, isError } = useFlagContextQuery();
  return isSuccess || isError;
}

/**
 * Gates asset ownership in the UI: the Ownership page, the owner panel,
 * chips, filters, bulk bar and the owner condition in asset groups. On
 * outside production; in production only with the localStorage override.
 */
export function useAssetOwnershipEnabled(): boolean {
  return isClientFeatureEnabled(ASSET_OWNERSHIP_FLAG);
}

/**
 * Gates the one AWS tile: the new AWS entry, the AWS page and the
 * organization section on the management integration's page. Decided in the
 * web app only, so the organization's backend flag list plays no part: in a
 * browser with the localStorage override set it is on, everywhere else it is
 * off until the flag is set to true. The backend routes are not gated.
 */
export function useAwsOrganizationOnboardingEnabled(): boolean {
  return isClientFeatureEnabled(AWS_ORGANIZATION_ONBOARDING_FLAG);
}

/**
 * Whether the asset inventory offers the package filter. Off, it hides the
 * button, the filter panel row and the SBOM tab's links, and the list ignores
 * the filter in the URL.
 */
export function useAssetPackageFilterEnabled(): boolean {
  return isClientFeatureEnabled(ASSET_PACKAGE_FILTER_FLAG);
}
